import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit2,
  Trash2,
  User,
  Users,
  UserCheck,
  Mail,
  Phone,
  Building2,
  Shield,
  Search,
  Key,
  Lock,
  Settings,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  ClipboardList,
} from 'lucide-react';
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut, updatePassword } from 'firebase/auth';
import { firestore } from '../../database/firebase';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { ToastContainer } from '../ui/Toast';
import {
  mergePermissions,
  validatePermissions,
  DEFAULT_PERMISSIONS,
} from '../../utils/permissions';
import { DeleteConfirmation } from '../ui/DeleteConfirmation';

const TeamMembersManagerV2 = ({
  userId,
  isReadOnly = false,
  externalAddModal = false,
  onExternalAddModalClose,
}) => {
  const { currentUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const isAddModalOpen = externalAddModal || showAddModal;
  const handleAddModalClose = () => {
    setShowAddModal(false);
    if (onExternalAddModalClose) {
      onExternalAddModalClose();
    }
  };

  const [editingMember, setEditingMember] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedMemberForPermissions, setSelectedMemberForPermissions] = useState(null);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [selectedMemberForPassword, setSelectedMemberForPassword] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [creatingAuth, setCreatingAuth] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const ROLES = [
    { value: 'founder', label: 'Founder', color: 'bg-purple-100 text-purple-800', icon: Shield },
    { value: 'admin', label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
    { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-800', icon: Building2 },
    { value: 'employee', label: 'Employee', color: 'bg-green-100 text-green-800', icon: User },
    { value: 'intern', label: 'Intern', color: 'bg-yellow-100 text-yellow-800', icon: UserCheck },
  ];

  const MAX_FOUNDERS = 3;
  const founderCount = teamMembers.filter(m => m.role === 'founder').length;

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const q = query(collection(firestore, 'teamMembers'), where('ownerId', '==', userId));
        const querySnapshot = await getDocs(q);

        const members = [];
        querySnapshot.forEach(doc => {
          members.push({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
          });
        });

        members.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTeamMembers(members);
      } catch (error) {
        toast.error('Failed to load team members:', error);
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, [userId]);

  const createFirebaseAuthAccount = async (email, password) => {
    try {
      const tempApp = initializeApp(
        {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        },
        'temp-client-employee-' + Date.now()
      );

      const tempAuth = getAuth(tempApp);
      const userCredential = await createUserWithEmailAndPassword(tempAuth, email, password);
      const uid = userCredential.user.uid;

      await tempAuth.signOut();
      return uid;
    } catch (error) {
      throw error;
    }
  };

  const addTeamMember = async memberData => {
    if (!userId) {
      toast.error('User not authenticated. Please refresh the page and try again.');
      return;
    }

    try {
      setCreatingAuth(true);
      let authUid = null;
      let tempPassword = null;

      // Create Firebase Auth account if createAccount is true
      if (memberData.createAccount) {
        tempPassword = memberData.tempPassword || `Client@${Date.now().toString().slice(-6)}`;
        try {
          authUid = await createFirebaseAuthAccount(memberData.email, tempPassword);
        } catch (authError) {
          if (authError.code === 'auth/email-already-in-use') {
            toast.error('A user with this email already exists');
            setCreatingAuth(false);
            return;
          }
          throw authError;
        }
      }

      // Merge permissions with defaults; founders get personality assessment by default
      let permissions = memberData.permissions
        ? mergePermissions(memberData.permissions, 'client')
        : mergePermissions(null, 'client');
      if (memberData.role === 'founder') {
        permissions = {
          ...permissions,
          profiling: {
            ...permissions.profiling,
            view: true,
            personalityAssessment: true,
            mindsetAssessment: true,
            businessHealthCheck: permissions.profiling?.businessHealthCheck ?? false,
          },
        };
      }

      const newMemberRef = doc(collection(firestore, 'teamMembers'));
      const isFounder = memberData.role === 'founder';
      if (isFounder && founderCount >= MAX_FOUNDERS) {
        toast.error(`Maximum ${MAX_FOUNDERS} founders allowed per company.`);
        setCreatingAuth(false);
        return;
      }

      const memberDataWithId = {
        ...memberData,
        id: newMemberRef.id,
        ownerId: userId,
        ownerEmail: currentUser?.email,
        authUid: authUid,
        permissions: permissions,
        employeeUser: true,
        userType: 'client-employee',
        isFounder: isFounder,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
      };

      // Remove temporary fields
      delete memberDataWithId.createAccount;
      delete memberDataWithId.tempPassword;

      await setDoc(newMemberRef, memberDataWithId);

      // Also create entry in clientEmployees collection for easier querying
      if (authUid) {
        await setDoc(doc(firestore, 'clientEmployees', authUid), {
          email: memberData.email,
          name: memberData.name,
          clientId: userId,
          clientEmail: currentUser?.email,
          permissions: permissions,
          userType: 'client-employee',
          isFounder: isFounder,
          role: memberData.role,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // If founder, add to clientFounders subcollection for personality assessments
      if (authUid && isFounder) {
        const founderRef = doc(collection(firestore, 'clients', userId, 'clientFounders'), authUid);
        await setDoc(founderRef, {
          email: memberData.email,
          name: memberData.name,
          authUid,
          role: 'founder',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      setTeamMembers(prev => [...prev, { ...memberDataWithId, createdAt: new Date() }]);
      toast.success('Team member added successfully');
      handleAddModalClose();

      if (authUid && tempPassword) {
        setGeneratedPassword(tempPassword);
        setShowPasswordModal(true);
      }
    } catch (error) {
      console.error('Error adding team member:', error);
      toast.error('Failed to add team member: ' + (error.message || 'Unknown error'));
    } finally {
      setCreatingAuth(false);
    }
  };

  const updateTeamMember = async (memberId, updatedData) => {
    if (!userId) {
      toast.error('User not authenticated. Please refresh the page and try again.');
      return;
    }

    try {
      const existingMember = teamMembers.find(m => m.id === memberId);
      const becomingFounder = updatedData.role === 'founder' && existingMember?.role !== 'founder';
      const otherFounders = teamMembers.filter(m => m.role === 'founder' && m.id !== memberId);
      if (becomingFounder && otherFounders.length >= MAX_FOUNDERS) {
        toast.error(`Maximum ${MAX_FOUNDERS} founders allowed per company.`);
        return;
      }

      // Merge permissions if provided
      if (updatedData.permissions) {
        updatedData.permissions = mergePermissions(updatedData.permissions, 'client');
      }

      updatedData.isFounder = updatedData.role === 'founder';

      const memberRef = doc(firestore, 'teamMembers', memberId);
      await setDoc(
        memberRef,
        {
          ...updatedData,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const member = teamMembers.find(m => m.id === memberId);
      if (member?.authUid) {
        await setDoc(
          doc(firestore, 'clientEmployees', member.authUid),
          {
            ...updatedData,
            isFounder: updatedData.isFounder,
            role: updatedData.role,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        // If now founder, ensure in clientFounders; if no longer founder, remove
        const founderRef = doc(
          collection(firestore, 'clients', userId, 'clientFounders'),
          member.authUid
        );
        if (updatedData.isFounder) {
          await setDoc(
            founderRef,
            {
              email: updatedData.email ?? member.email,
              name: updatedData.name ?? member.name,
              authUid: member.authUid,
              role: 'founder',
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        } else {
          try {
            await deleteDoc(founderRef);
          } catch (e) {
            console.warn('Remove from clientFounders:', e);
          }
        }
      }

      setTeamMembers(prev =>
        prev.map(member =>
          member.id === memberId ? { ...member, ...updatedData, updatedAt: new Date() } : member
        )
      );
      toast.success('Team member updated successfully');
      setShowEditModal(false);
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating team member:', error);
      toast.error('Failed to update team member:', error);
    }
  };

  const handleDeleteClick = member => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'teamMembers', memberToDelete.id));

      if (memberToDelete?.authUid) {
        try {
          await deleteDoc(doc(firestore, 'clientEmployees', memberToDelete.authUid));
        } catch (error) {
          console.warn('Failed to delete from clientEmployees:', error);
        }
        if (memberToDelete.role === 'founder') {
          try {
            const founderRef = doc(
              collection(firestore, 'clients', userId, 'clientFounders'),
              memberToDelete.authUid
            );
            await deleteDoc(founderRef);
          } catch (error) {
            console.warn('Failed to delete from clientFounders:', error);
          }
        }
      }

      setTeamMembers(prev => prev.filter(member => member.id !== memberToDelete.id));
      toast.success('Team member deleted successfully');
      setShowDeleteModal(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member:', error);
    }
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch =
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.position?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = filterRole === 'all' || member.role === filterRole;

    return matchesSearch && matchesRole;
  });

  const getRoleInfo = role => {
    return ROLES.find(r => r.value === role) || ROLES[2];
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-slate-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Team Members</h2>
          <p className="text-slate-600">Manage your team members and their roles</p>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 rounded-xl bg-slate-900 px-4 py-2 text-white transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            <span>Add Member</span>
          </button>
        )}
      </div>

      {teamMembers.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-blue-100 p-2">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Total Members</p>
                <p className="text-2xl font-bold text-slate-900">{teamMembers.length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-green-100 p-2">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Active</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teamMembers.filter(m => m.status === 'active').length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-red-100 p-2">
                <Shield className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Admins</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teamMembers.filter(m => m.role === 'admin').length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-yellow-100 p-2">
                <Building2 className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Managers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {teamMembers.filter(m => m.role === 'manager').length}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center">
              <div className="rounded-lg bg-purple-100 p-2">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-600">Founders</p>
                <p className="text-2xl font-bold text-slate-900">
                  {founderCount}/{MAX_FOUNDERS}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {teamMembers.length > 0 && (
        <div className="rounded-xl border bg-white p-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-slate-400" />
              <input
                type="text"
                placeholder="Search team members..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={filterRole}
              onChange={e => setFilterRole(e.target.value)}
              className="rounded-lg border px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {teamMembers.length === 0 ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 p-6">
              <Users className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="mb-3 text-xl font-semibold text-gray-900">No members or employees</h3>
            <p className="mx-auto mb-6 max-w-md text-gray-600">
              Start building your team by adding your first team member.
            </p>
            {!isReadOnly && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center space-x-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-lg transition-colors hover:bg-blue-700"
              >
                <Plus className="h-5 w-5" />
                <span>Add Member</span>
              </button>
            )}
          </motion.div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Phone
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Gender
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                    Age Bracket
                  </th>
                  {!isReadOnly && (
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                      Actions
                    </th>
                  )}
                  {teamMembers.some(m => m.employeeUser && m.authUid) && (
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                      Account
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredMembers.map(member => (
                  <TeamMemberRow
                    key={member.id}
                    member={member}
                    onEdit={() => {
                      setEditingMember(member);
                      setShowEditModal(true);
                    }}
                    onPermissions={() => {
                      setSelectedMemberForPermissions(member);
                      setShowPermissionsModal(true);
                    }}
                    onDelete={() => handleDeleteClick(member)}
                    isReadOnly={isReadOnly}
                    getRoleInfo={getRoleInfo}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {isAddModalOpen && (
          <TeamMemberModal
            isOpen={isAddModalOpen}
            onClose={handleAddModalClose}
            onSave={addTeamMember}
            roles={ROLES}
            title="Add Team Member"
            maxFoundersReached={founderCount >= MAX_FOUNDERS}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && editingMember && (
          <TeamMemberModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingMember(null);
            }}
            onSave={data => updateTeamMember(editingMember.id, data)}
            initialData={editingMember}
            roles={ROLES}
            title="Edit Team Member"
            maxFoundersReached={founderCount >= MAX_FOUNDERS && editingMember.role !== 'founder'}
          />
        )}
      </AnimatePresence>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMemberToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Team Member"
        message="Are you sure you want to delete this team member? This action cannot be undone."
        itemName={memberToDelete?.name}
      />

      {/* Password Display Modal */}
      <AnimatePresence>
        {showPasswordModal && generatedPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Account Created Successfully</h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-4 rounded-lg bg-blue-50 p-4">
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Temporary password for login:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-lg font-bold text-blue-600">
                    {generatedPassword}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPassword);
                      toast.success('Password copied to clipboard');
                    }}
                    className="rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700"
                  >
                    <Key className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                Share this password with the employee. They should change it after first login from
                their profile settings.
              </p>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permissions Configuration Modal */}
      <AnimatePresence>
        {showPermissionsModal && selectedMemberForPermissions && (
          <ClientPermissionsModal
            member={selectedMemberForPermissions}
            onClose={() => {
              setShowPermissionsModal(false);
              setSelectedMemberForPermissions(null);
            }}
            onSave={permissions => {
              updateTeamMember(selectedMemberForPermissions.id, { permissions });
              setShowPermissionsModal(false);
              setSelectedMemberForPermissions(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Password Change Modal */}
      <AnimatePresence>
        {showPasswordChangeModal && selectedMemberForPassword && (
          <PasswordChangeModal
            member={selectedMemberForPassword}
            onClose={() => {
              setShowPasswordChangeModal(false);
              setSelectedMemberForPassword(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Team Member Row Component (for table display)
const TeamMemberRow = ({ member, onEdit, onDelete, onPermissions, isReadOnly, getRoleInfo }) => {
  const roleInfo = getRoleInfo(member.role);
  const RoleIcon = roleInfo.icon;

  return (
    <tr className="transition-colors hover:bg-slate-50">
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center">
          <div className="mr-3 rounded-full bg-blue-100 p-2">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{member.name || 'No Name'}</div>
            <div className="text-xs text-slate-500">{member.position || 'No Position'}</div>
          </div>
        </div>
      </td>
      {/* Role */}
      <td className="whitespace-nowrap px-6 py-4">
        <span
          className={`inline-flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium ${roleInfo.color}`}
        >
          <RoleIcon className="h-3 w-3" />
          <span>{roleInfo.label}</span>
        </span>
      </td>
      {/* Email */}
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center text-sm text-slate-900">
          <Mail className="mr-2 h-4 w-4 text-slate-400" />
          {member.email || 'No email'}
        </div>
      </td>
      {/* Phone */}
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center text-sm text-slate-900">
          <Phone className="mr-2 h-4 w-4 text-slate-400" />
          {member.phone || 'No phone'}
        </div>
      </td>
      {/* Gender */}
      <td className="whitespace-nowrap px-6 py-4">
        <span className="text-sm text-slate-900">{member.gender || '—'}</span>
      </td>
      {/* Age Bracket */}
      <td className="whitespace-nowrap px-6 py-4">
        <span className="text-sm text-slate-900">{member.ageBracket || '—'}</span>
      </td>
      {!isReadOnly && (
        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
          <div className="flex flex-wrap gap-2">
            {member.employeeUser && member.authUid && onPermissions && (
              <button
                onClick={onPermissions}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100"
                title="Manage Permissions"
              >
                <Settings className="h-3 w-3" />
                Permissions
              </button>
            )}
            <button
              onClick={onEdit}
              className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100"
            >
              <Edit2 className="mr-1 h-3 w-3" />
              Edit
            </button>
            <button
              onClick={onDelete}
              className="inline-flex items-center rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-100"
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </button>
          </div>
        </td>
      )}
      {member.employeeUser && member.authUid && (
        <td className="whitespace-nowrap px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
              <CheckCircle className="h-3 w-3" />
              Has Account
            </span>
          </div>
        </td>
      )}
    </tr>
  );
};

// Team Member Modal Component
const TeamMemberModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  roles,
  title,
  maxFoundersReached = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'employee',
    position: '',
    status: 'active',
    gender: 'Male',
    ageBracket: 'Under 35',
    createAccount: false,
    permissions: null,
    ...initialData,
  });
  const [loading, setLoading] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
              onClick={e => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className="relative bg-blue-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{title}</h2>
                    <p className="mt-1 text-sm text-blue-100">
                      {initialData
                        ? 'Update employee details'
                        : 'Add a new employee to your organization'}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg p-2 text-white/90 transition-all hover:bg-white/20 hover:text-white"
                    type="button"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div
                className="overflow-y-auto px-6 py-6"
                style={{ maxHeight: 'calc(90vh - 200px)' }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                      <User className="h-5 w-5 text-blue-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Basic Information</h4>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <User className="h-4 w-4 text-gray-400" />
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={e => handleChange('name', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="John Doe"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Shield className="h-4 w-4 text-gray-400" />
                          Role <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={formData.role}
                          onChange={e => handleChange('role', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                          {roles.map(role => (
                            <option
                              key={role.value}
                              value={role.value}
                              disabled={role.value === 'founder' && maxFoundersReached}
                            >
                              {role.label}
                              {role.value === 'founder' && maxFoundersReached ? ' (max 3)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Mail className="h-4 w-4 text-gray-400" />
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={e => handleChange('email', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="john.doe@example.com"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <Phone className="h-4 w-4 text-gray-400" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={e => handleChange('phone', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <User className="h-4 w-4 text-gray-400" />
                          Gender
                        </label>
                        <select
                          value={formData.gender}
                          onChange={e => handleChange('gender', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                          <UserCheck className="h-4 w-4 text-gray-400" />
                          Age Bracket
                        </label>
                        <select
                          value={formData.ageBracket}
                          onChange={e => handleChange('ageBracket', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                          <option value="18-25">18-25</option>
                          <option value="26-35">26-35</option>
                          <option value="36-45">36-45</option>
                          <option value="46-55">46-55</option>
                          <option value="56-65">56-65</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Account Creation Section */}
                  {!initialData && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                        <Key className="h-5 w-5 text-blue-600" />
                        <h4 className="text-lg font-semibold text-gray-900">Account Access</h4>
                      </div>

                      <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-5 transition-all hover:border-gray-400">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                              <input
                                type="checkbox"
                                id="createAccount"
                                checked={formData.createAccount}
                                onChange={e => {
                                  handleChange('createAccount', e.target.checked);
                                  if (e.target.checked) {
                                    handleChange('permissions', mergePermissions(null, 'client'));
                                  } else {
                                    handleChange('permissions', null);
                                  }
                                }}
                                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <label htmlFor="createAccount" className="block cursor-pointer">
                              <div className="flex items-center gap-2">
                                <span className="text-base font-bold text-gray-900">
                                  Create Login Account
                                </span>
                                {formData.createAccount && (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                    Enabled
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-gray-600">
                                Enable this employee to sign in independently with their own
                                credentials. They will access only the features and data you permit.
                              </p>
                            </label>
                            {formData.createAccount && (
                              <motion.button
                                type="button"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={() => setShowPermissionsModal(true)}
                                className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg"
                              >
                                <Settings className="h-4 w-4" />
                                Configure Permissions
                                {formData.permissions && (
                                  <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                                    {
                                      Object.keys(formData.permissions).filter(
                                        k => k !== 'enabled' && formData.permissions[k]?.view
                                      ).length
                                    }{' '}
                                    sections
                                  </span>
                                )}
                              </motion.button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-xl border-2 border-gray-300 bg-white px-6 py-3 font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 disabled:hover:bg-blue-600"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                          />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <UserCheck className="h-4 w-4" />
                          {initialData ? 'Update Member' : 'Add Member'}
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permissions Modal Integration */}
      <AnimatePresence>
        {showPermissionsModal && (
          <ClientPermissionsModal
            member={{ ...formData, name: formData.name || 'New Employee' }}
            onClose={() => setShowPermissionsModal(false)}
            onSave={permissions => {
              handleChange('permissions', permissions);
              setShowPermissionsModal(false);
              toast.success('Permissions configured successfully');
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Permissions Configuration Modal for Client Employees
const ClientPermissionsModal = ({ member, onClose, onSave }) => {
  const [permissions, setPermissions] = useState(
    member.permissions || mergePermissions(null, 'client')
  );
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = category => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const togglePermission = (category, action) => {
    setPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [action]: !prev[category]?.[action],
      },
    }));
  };

  const toggleCategoryAll = (category, value) => {
    const categoryPerms = permissions[category] || {};
    const newCategoryPerms = {};
    Object.keys(categoryPerms).forEach(action => {
      newCategoryPerms[action] = value;
    });
    setPermissions(prev => ({
      ...prev,
      [category]: newCategoryPerms,
    }));
  };

  const handleSave = () => {
    const validation = validatePermissions(permissions);
    if (!validation.valid) {
      toast.error('Invalid permissions: ' + validation.errors.join(', '));
      return;
    }
    onSave(permissions);
  };

  const permissionCategories = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      actions: ['view', 'edit'],
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: Shield,
      actions: ['view', 'upload', 'delete'],
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: Building2,
      actions: ['view', 'create', 'edit'],
    },
    {
      id: 'programs',
      label: 'Programs',
      icon: UserCheck,
      actions: ['view', 'enroll'],
    },
    {
      id: 'training',
      label: 'Training Modules',
      icon: UserCheck,
      actions: [
        'view',
        'designABusiness',
        'costingAndManagement',
        'productDevelopment',
        'commercialisation',
        'scaling',
        'funding',
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: Search,
      actions: ['view', 'export'],
    },
    {
      id: 'team',
      label: 'Team & Employees',
      icon: Users,
      actions: ['view', 'manage', 'addEmployees', 'editPermissions'],
    },
    {
      id: 'profiling',
      label: 'Profiling & Assessments',
      icon: ClipboardList,
      actions: ['view', 'personalityAssessment', 'mindsetAssessment', 'businessHealthCheck'],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      actions: ['view', 'edit'],
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Configure Permissions</h3>
            <p className="mt-1 text-sm text-gray-600">
              Set what {member.name} can view and manage in the client dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enablePermissions"
              checked={permissions.enabled !== false}
              onChange={e => setPermissions(prev => ({ ...prev, enabled: e.target.checked }))}
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="enablePermissions" className="text-sm font-semibold text-gray-900">
              Enable Access Control
            </label>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              permissions.enabled !== false
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {permissions.enabled !== false ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {permissions.enabled !== false && (
          <div className="space-y-3">
            {permissionCategories.map(category => {
              const CategoryIcon = category.icon;
              const categoryPerms = permissions[category.id] || {};
              const allChecked = category.actions.every(action => categoryPerms[action] === true);
              const someChecked = category.actions.some(action => categoryPerms[action] === true);
              const isExpanded = expandedCategories[category.id];

              return (
                <div
                  key={category.id}
                  className="rounded-lg border border-gray-200 bg-white transition-all hover:border-blue-300"
                >
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <CategoryIcon className="h-5 w-5 text-gray-600" />
                      <span className="font-semibold text-gray-900">{category.label}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          allChecked
                            ? 'bg-green-100 text-green-800'
                            : someChecked
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {category.actions.filter(a => categoryPerms[a]).length}/
                        {category.actions.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={input => {
                          if (input) input.indeterminate = someChecked && !allChecked;
                        }}
                        onChange={e => {
                          e.stopPropagation();
                          toggleCategoryAll(category.id, e.target.checked);
                        }}
                        onClick={e => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {isExpanded ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {category.actions.map(action => (
                          <label
                            key={action}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={categoryPerms[action] === true}
                              onChange={() => togglePermission(category.id, action)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium capitalize text-gray-700">
                              {action.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white hover:bg-blue-700"
          >
            Save Permissions
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Password Change Modal (for employees to change their own password)
const PasswordChangeModal = ({ member, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // Note: This requires the employee to be signed in
      // In a real implementation, you'd need to sign them in first or use Firebase Admin SDK
      // For now, we'll show a message that they need to be signed in
      toast.info(
        'Password change requires the employee to be signed in. They can change it from their profile settings.'
      );
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
      setError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Change Password</h3>
            <p className="mt-1 text-sm text-gray-600">Update password for {member.name}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</div>}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new password (min 6 characters)"
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm new password"
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default TeamMembersManagerV2;
